// src/pages/reports/services/callbacks.service.js
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../../../firebaseConfig';

/**
 * saveCallAttempt
 * - Writes a callbacks subdoc under the lead
 * payload: { called, answered, priceOffered, newPrice (number|null), jobWon, notes, nextCallDate }
 * Returns the written payload (with server timestamp).
 */
export async function saveCallAttempt({ orgId, leadId, payload, user }) {
  const byName = user?.displayName || user?.email || 'Admin';

  const data = {
    at: serverTimestamp(),
    byUid: user?.uid || null,
    byName,
    called: !!payload.called,
    answered: !!payload.answered,
    priceOffered: !!payload.priceOffered,
    newPrice: payload.newPrice == null || payload.newPrice === '' ? null : Number(payload.newPrice),
    jobWon: !!payload.jobWon,
    notes: (payload.notes || '').trim(),
    nextCallDate: payload.nextCallDate || null,
  };

  const cbRef = collection(db, 'organizations', orgId, 'leads', leadId, 'callbacks');
  await addDoc(cbRef, data);
  return data;
}

/**
 * convertToSold
 * Converts a lead to SOLD and (optionally) applies a new final contract price.
 *
 * Params:
 * - orgId, leadId, user
 * - priceOverride?: number  // final GBP price (VAT-inclusive). If provided, becomes the contract price.
 *
 * Effects:
 * - Lead:
 *    status = 'SOLD'
 *    soldAt = serverTimestamp()
 *    contractPrice = priceOverride (if provided)
 *    recoveredFrom = 'CANCELLED' (only if previous status was CANCELLED/CANCELED)
 *    statusHistory += { from, to, byUid, at }
 * - Sales Report (current):
 *    ensure doc exists
 *    if priceOverride provided:
 *      contractPrice = priceOverride
 *      priceAdjustments += { from, to, reason, by, at }
 */
export async function convertToSold({ orgId, leadId, user, priceOverride }) {
  const byName = user?.displayName || user?.email || 'Admin';
  const byUid = user?.uid || null;

  const leadRef = doc(db, 'organizations', orgId, 'leads', leadId);
  const repRef = doc(db, 'organizations', orgId, 'leads', leadId, 'salesReport', 'current');

  // Read current lead + report
  const [leadSnap, repSnap] = await Promise.all([getDoc(leadRef), getDoc(repRef)]);
  const lead = leadSnap.exists() ? (leadSnap.data() || {}) : {};
  const rep = repSnap.exists() ? (repSnap.data() || {}) : {};

  const prevStatus = String(lead.status || '').toUpperCase();
  const prevLeadPrice =
    typeof lead.contractPrice === 'number' ? lead.contractPrice :
    (lead.contractPrice != null ? Number(lead.contractPrice) : null);
  const prevRepPrice =
    typeof rep.contractPrice === 'number' ? rep.contractPrice :
    (rep.contractPrice != null ? Number(rep.contractPrice) : null);
  const previousPrice = prevRepPrice != null ? prevRepPrice : prevLeadPrice;

  // Build status history
  const hist = Array.isArray(lead.statusHistory) ? [...lead.statusHistory] : [];
  hist.push({
    at: new Date().toISOString(),
    byUid,
    from: prevStatus,
    to: 'SOLD',
  });

  // Lead updates (always convert to SOLD)
  const leadUpdates = {
    status: 'SOLD',
    soldAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    salespersonId: byUid || '',
    salespersonName: byName,
    statusHistory: hist,
    postSaleStage: 'awaiting_survey',
    lastActivityAt: serverTimestamp(),
  };

  // If converting from a cancelled state, note recovery
  if (prevStatus === 'CANCELLED' || prevStatus === 'CANCELED') {
    leadUpdates.recoveredFrom = 'CANCELLED';
  }

  // Apply final contract price if provided
  const hasOverride =
    priceOverride != null &&
    priceOverride !== '' &&
    !Number.isNaN(Number(priceOverride)) &&
    Number(priceOverride) > 0;

  if (hasOverride) {
    leadUpdates.contractPrice = Number(priceOverride);
  }

  await updateDoc(leadRef, leadUpdates);

  // Ensure salesReport/current exists
  if (!repSnap.exists()) {
    await setDoc(
      repRef,
      {
        status: 'draft',
        createdAt: serverTimestamp(),
        createdByUid: byUid,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  // If a final price was provided, write it to the report and append a priceAdjustments entry
  if (hasOverride) {
    const reason =
      prevStatus === 'CANCELLED' || prevStatus === 'CANCELED'
        ? 'Recovery after cancellation'
        : 'Price set on conversion';

    const repUpdates = {
      contractPrice: Number(priceOverride),
      updatedAt: serverTimestamp(),
      priceAdjustments: arrayUnion({
        from: previousPrice ?? null,
        to: Number(priceOverride),
        reason,
        by: byUid,
        at: new Date().toISOString(),
      }),
    };

    await updateDoc(repRef, repUpdates);
  }
}

/**
 * markDnc (Do Not Contact)
 * - Sets ARCHIVE_DNC + reason/note and appends statusHistory
 */
export async function markDnc({ orgId, leadId, reason, note, user }) {
  const leadRef = doc(db, 'organizations', orgId, 'leads', leadId);
  const snap = await getDoc(leadRef);
  const lead = snap.data() || {};
  const prevStatus = (lead.status || '').toUpperCase();
  const history = Array.isArray(lead.statusHistory) ? [...lead.statusHistory] : [];
  history.push({
    at: new Date().toISOString(),
    byUid: user?.uid || null,
    from: prevStatus,
    to: 'ARCHIVE_DNC',
  });

  await updateDoc(leadRef, {
    status: 'ARCHIVE_DNC',
    doNotContact: true,
    dncReason: reason || 'Do Not Contact',
    dncNote: (note || '').trim(),
    dncAt: serverTimestamp(),
    dncBy: { uid: user?.uid || null, name: user?.displayName || user?.email || 'Admin' },
    updatedAt: serverTimestamp(),
    lastActivityAt: serverTimestamp(),
    statusHistory: history,
  });
}
